import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AddOnOptionSelections, Product } from '@la/types';
import { RegistrationAddOnsState } from 'domains/MemberRegistration/MemberRegistration.types';

const initialState: RegistrationAddOnsState = {
  addOnProducts: [],
  registrationCost: undefined,
  registrationId: undefined,
  registrationType: undefined,
  selectionsWithErrors: [],
  taxRate: 0,
  userSelections: [],
};

const registrationAddOnsSlice = createSlice({
  name: 'registrationAddOns',
  initialState,
  reducers: {
    resetRegistrationAddOns: () => initialState,
    setRegistrationAddOns(state, action: PayloadAction<Product[]>) {
      state.addOnProducts = action.payload;
    },
    setRegistrationCost(state, action: PayloadAction<number>) {
      state.registrationCost = action.payload;
    },
    setRegistrationType(state, action: PayloadAction<'player' | 'staff'>) {
      state.registrationType = action.payload;
    },
    setSelectionsWithErrors(state, action: PayloadAction<string[]>) {
      state.selectionsWithErrors = action.payload;
    },
    setTaxRate(state, action: PayloadAction<number>) {
      state.taxRate = action.payload;
    },
    setUserSelections(state, action: PayloadAction<AddOnOptionSelections[]>) {
      state.userSelections = action.payload;
    },
    // This should only be used by required add-ons.
    updateRequiredAddOnSelection(
      state,
      action: PayloadAction<{
        addOnId: number;
        propertyId: string;
        value: string;
      }>
    ) {
      const { addOnId, propertyId, value } = action.payload;

      return {
        ...state,
        userSelections: state.userSelections.map((userSelection) => {
          // Only update the matching addOnId
          if (userSelection.addOnId !== addOnId) {
            return userSelection;
          }

          // Find existing selection with matching optionId
          const existingSelectionIndex = userSelection.selections.findIndex(
            (selection) => selection.optionId === propertyId
          );

          // If value is missing, remove existing selection or don't add new one
          if (!value) {
            if (existingSelectionIndex !== -1) {
              // Remove existing selection
              return {
                ...userSelection,
                selections: userSelection.selections.filter(
                  (selection, index) => index !== existingSelectionIndex
                ),
              };
            }
            // No existing selection and missing value, return as is
            return userSelection;
          }

          // If selection exists, update its selectedValue
          if (existingSelectionIndex !== -1) {
            return {
              ...userSelection,
              selections: userSelection.selections.map((selection, index) =>
                index === existingSelectionIndex
                  ? { ...selection, selectedValue: value }
                  : selection
              ),
            };
          }

          // If selection doesn't exist and value is valid, add new one
          return {
            ...userSelection,
            selections: [
              ...userSelection.selections,
              { optionId: propertyId, selectedValue: value },
            ],
          };
        }),
      };
    },
    updateOptionalAddOns(
      state,
      action: PayloadAction<{
        updatedUserSelections: AddOnOptionSelections[];
      }>
    ) {
      const { updatedUserSelections } = action.payload;

      return {
        ...state,
        userSelections: updatedUserSelections,
      };
    },
  },
});

export const {
  resetRegistrationAddOns,
  setRegistrationAddOns,
  setRegistrationType,
  setSelectionsWithErrors,
  setTaxRate,
  setUserSelections,
  updateRequiredAddOnSelection,
  updateOptionalAddOns,
} = registrationAddOnsSlice.actions;

export default registrationAddOnsSlice.reducer;
